import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useOutletContext, useParams } from 'react-router-dom';
import { API, AppConstants } from '@enrich/shared-lib';
import QueryString from 'qs';

const TITLES = {
    'incomplete-resume': 'Incomplete Resume',
    'incomplete-coach-meeting': 'Incomplete Coach Meeting',
    'unresponsive': 'Unresponsive',
};

export const NonActiveStudentsList = ({ filter }) => {
    const user = useOutletContext();
    const { schoolSlug } = useParams();
    const school = user.school;
    const stepOptions = [25, 50, 100];
    const [students, setStudents] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadStudents({
                schoolId: school.id,
                filter,
                page,
                pageSize,
            });

            if (isActive) {
                setStudents(response.data);
                setTotalItems(response.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [school.id, filter, page, pageSize]);

    const contactDataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => (
                <Anchor
                    href={`/${schoolSlug}/students/${contact.id}`}
                    label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Program Status',
            property: 'attributes.enrichProgramStatus',
            render: (contact) => contact.attributes.enrichProgramStatus,
            sortable: true,
        },
        {
            header: 'Last Login',
            property: 'attributes.user.data.attributes.lastLogin',
            render: (contact) => {
                const lastLogin =
                    contact.attributes.user?.data?.attributes?.lastLogin;
                if (!lastLogin) return '';
                return new Date(lastLogin).toLocaleString(undefined, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                });
            },
            sortable: true,
        },
    ];

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
            setPage(1);
            return;
        }
        setPage(page);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box>
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">
                            Non-Active Students &mdash; {TITLES[filter]}
                        </Heading>
                    </Box>
                    <Box elevation="large" pad="medium" fill="horizontal">
                        <DataTable
                            primaryKey="id"
                            sortable
                            data={students}
                            columns={contactDataColumns}
                        />
                    </Box>
                </Box>
                <Box align="center" justify="center" pad="small">
                    <Pagination
                        numberItems={totalItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handlePageChange({ page, startIndex, endIndex });
                        }}
                        step={pageSize}
                        stepOptions={stepOptions}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

const buildFilters = (schoolId, filter) => {
    const base = {
        enrichProgramStatus: {
            $in: AppConstants.nonActiveStudentStatuses,
        },
        school: schoolId,
    };

    if (filter === 'incomplete-resume') {
        return { ...base, isResumeReady: { $eq: false } };
    }
    if (filter === 'incomplete-coach-meeting') {
        return { ...base, coachMeetingDatetime: { $null: true } };
    }
    if (filter === 'unresponsive') {
        return { ...base, communicationLevel: { $eq: 0 } };
    }
    return base;
};

const loadStudents = async ({ schoolId, filter, page, pageSize }) => {
    const query = QueryString.stringify({
        filters: buildFilters(schoolId, filter),
        populate: { user: { fields: ['lastLogin'] } },
        pagination: { page, pageSize },
        sort: ['updatedAt:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
        return { data: [], meta: { pagination: { page: 1, total: 0 } } };
    }
};
